import React, { ReactElement, CSSProperties } from "react";
import {
    GoogleOutlined,
    HomeOutlined,
    TeamOutlined,
    MailOutlined,
} from '@ant-design/icons';
import {
    Tooltip,
    Row,
    Col,
    Avatar,
} from 'antd';
import { Gutter } from "antd/lib/grid/row";
import {Link, useHistory, useLocation} from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';

import {Department, Researcher, RootState} from "utils/types";
import searchBlockS from 'features/content/search_page/components/SearchBlock/SearchBlockSlice';
import './ResearcherListTitle.css';
import { SDGIconBackgroundColors, SDGs } from 'assets/svgs/SDG_icons';
import C3IIcon from 'assets/icons/C3I';
import searchPgUserInputsS from 'features/content/search_page/UserInputsSlice';
import { allBeforeCurrentYear, getRidOfOutdatedYears } from "utils/restrictYears";


interface ResearcherTitleProps {
    researcher: Researcher,
    profilePage?: boolean
}

const ResearcherListTitle = ({ researcher, profilePage }: ResearcherTitleProps) => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const avatarSize = researcher && profilePage ? 26 : 18;
    const titleRowGutter: Gutter | [Gutter, Gutter] | undefined = researcher && profilePage ? [0, 4] : [0, 0];
    const styles: Record<string, CSSProperties> = researcher && profilePage ? {
        faculties: {
            paddingTop: '12px',
            fontSize: 16,
        },
        name: {
            color: '#000000',
            marginRight: researcher.show_preferred_pronouns && researcher.preferred_pronouns ? 4 : '20px',
            fontSize: '24px'
        },
        preferred_pronouns: {
            fontSize: '24px',
            marginRight: '20px',
        },
        sfu_link: {
            marginRight: '8px',
            fontSize: '24px'
        },
        google_scholar_link: {
            marginRight: '8px',
            fontSize: '24px'
        },
        accepting_graduate_student: {
            marginRight: '8px',
        },
        primary_campus: {
            color: '#666',
            fontSize: '22px'
        },
        preferred_email: {
            marginRight: '8px',
            fontSize: '24px'
        },
        avatar: {
            backgroundColor: '#87d068',
            fontSize: '20px',
            top: '-4px'
        },
        interestsRow: {
            paddingTop: '16px',
            fontSize: 14,
        },
        SDGvecRow: {
            paddingTop: '16px'
        }
    } : {
        faculties: {
            paddingTop: '0px'
        },
        name: {
            color: '#000000',
            marginRight: '16px',
            fontSize: '18px'
        },
        sfu_link: {
            marginRight: '8px',
            fontSize: '16px'
        },
        google_scholar_link: {
            marginRight: '8px',
            fontSize: '16px'
        },
        accepting_graduate_student: {
            marginRight: '8px',
        },
        preferred_email: {
            marginRight: '8px',
            fontSize: '16px'
        },
        avatar: {
            backgroundColor: '#87d068',
            fontSize: '14px',
            top: '-2px',
            marginRight: profilePage?8:0,
        },
        interestsRow: {
            fontSize: 12,
            paddingTop: '2px',
        },
        SDGvecRow: {
            paddingTop: '2px',
        }
    };

    let name;
    if (researcher && researcher.preferred_first_name && researcher.preferred_last_name) {
        name = `${researcher.preferred_last_name}, ${researcher.preferred_first_name}`
    } else {
        name = ''
    }

    const {
        keywordType,
    } = useSelector((state: RootState) => state.searchPgUserInputs);

    const faculties: any = {};
    const idsForSingleDepartment: string[] = [
        '14', // Beedie School of Business
        '10', // Faculty of Education
        '51' // Faculty of Health Sciences
    ];
    if (researcher && researcher.departments) {
        const sortedDepartments: Department[] = researcher.departments.slice().sort((d1, d2) => {
            return d1.name === d2.name ? 0
                : d1.name < d2.name ? -1 : 1;
        });
        sortedDepartments.forEach((department: Department) => {
            if (faculties[department.faculty.name] === undefined) {
                faculties[department.faculty.name] = {
                    link: department.faculty.link,
                    departments: []
                };
            }
            if (!idsForSingleDepartment.includes(department.id)) {
                faculties[department.faculty.name].departments.push({
                    name: department.name,
                    link: department.link
                });
            }
        });
    }
    /* 'faculties' object's structure example:
    {
        "Faculty of Science": {
            "link": "http://www.sfu.ca/science.html",
            "departments": [
                {
                    "name": "Department of Molecular Biology and Biochemistry",
                    "link": "http://www.sfu.ca/mbb"
                },
                {
                    "name": "Department of Chemistry",
                    "link":"https://www.sfu.ca/chemistry.html"
                }
            ]
        },
        "Faculty of Education": {
            "link": "https://www.sfu.ca/education.html",
            "departments": []
        }
    } 
    */
    const facultiesSortedList: string[] = Object.keys(faculties).sort();
    const facultiesJSXElements = facultiesSortedList.map((facName, fIndex) => (
        <div key={fIndex} style={styles.faculties}>
            <a href={faculties[facName].link} target='_blank' rel="noopener noreferrer">
                <strong className="sfu-grey-link">{facName}</strong>
            </a>
            <br />
            {faculties[facName].departments.map((dep: {name: string; link: string}, dIndex: number) => (
                <div key={dIndex}>
                    <a className="sfu-black-link" href={dep.link} target='_blank' rel="noopener noreferrer">
                        {dep.name}
                    </a>
                    <br />
                </div>
            ))}
        </div>
    ));

    const onInterestClick = (event: any) => {

        // this is for
        // when user click the interest icon, he is not at the home page (search page), e.g. profile page
        if (location.pathname !== '/') { 
            dispatch(searchPgUserInputsS.actions.reset());
            history.push('/');
        }

        dispatch(searchBlockS.actions.setSearchInput_internal(event.target.innerText));
        dispatch(searchPgUserInputsS.actions.setSearchInput(event.target.innerText));
        dispatch(searchPgUserInputsS.actions.setKeywordType('search'));
        window.scrollTo(0, 0);
    };

    const onSDGInterestClick = (itemIndex: number) => {
        // this is for
        // when user click the interest icon, he is not at the home page (search page), e.g. profile page
        if (location.pathname !== '/') { 
            dispatch(searchBlockS.actions.reset());
            dispatch(searchPgUserInputsS.actions.reset());
            history.push('/');
        }

        dispatch(searchPgUserInputsS.actions.setSDG(0));// 0 means clear the SDGs
        dispatch(searchPgUserInputsS.actions.setSDG(itemIndex));
        window.scrollTo(0, 0);

        if (keywordType === 'match') {
            dispatch(searchPgUserInputsS.actions.setKeywordType('search'));
            dispatch(searchBlockS.actions.setSearchInput_internal(''));
            dispatch(searchPgUserInputsS.actions.setSearchInput(''));
        }

    };

    // Assumption: Research keywords are separated by a comma
    const interests = researcher.interests && researcher.interests.split(',').map((keyword, index) => (
        <span className="interests" key={index} onClick={onInterestClick}>
            {keyword.trim()}
        </span>
    ));

    const title = (
        <div className={researcher && profilePage?'rsrProfilePage':'rsrListPage'}>
            <Row gutter={titleRowGutter} justify="space-between">
                <Row gutter={titleRowGutter}>
                    <Col>
                        <Link to={`/profile/${researcher.random_id}`}>
                            <span className="sub-heading-font" style={styles.name}>
                                <strong className="sfu-black-link">{name}</strong>
                            </span>
                        </Link>

                        {researcher.show_preferred_pronouns && researcher.preferred_pronouns && profilePage &&
                        <span style={styles.preferred_pronouns}>
                            ({researcher.preferred_pronouns})
                        </span>}
                    </Col>

                    <Col>
                        {researcher.sfu_link &&
                        <a style={styles.sfu_link}
                            className="sfu-grey-link"
                            href={researcher.sfu_link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Tooltip
                                overlayClassName="sub-heading-font"
                                title='Researcher Website'
                            >
                                <HomeOutlined/>
                            </Tooltip>
                        </a>}

                        {researcher.google_scholar_link &&
                        <a style={styles.google_scholar_link}
                            className="sfu-grey-link"
                            href={researcher.google_scholar_link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Tooltip
                                overlayClassName="sub-heading-font"
                                title='Google Scholar'
                            >
                                <GoogleOutlined/>
                            </Tooltip>
                        </a>}

                        {researcher.show_preferred_email && researcher.preferred_email &&
                        <a style={styles.preferred_email}
                            className="sfu-grey-link"
                            href={'mailto:' + researcher.preferred_email}
                        >
                            <Tooltip
                                overlayClassName="sub-heading-font"
                                title='Email'
                            >
                                <MailOutlined />
                            </Tooltip>
                        </a>}

                        {researcher.recruitment && 
                         researcher.recruitment.accepting_graduate_student &&
                         (!allBeforeCurrentYear(researcher.recruitment.years)) &&
                        <span style={styles.accepting_graduate_student}>
                            <Tooltip
                                overlayClassName="sub-heading-font"
                                title={getRidOfOutdatedYears(researcher.recruitment.years).length>0?
                                    <>
                                        Accepting Graduate Students <br/>
                                        {getRidOfOutdatedYears(researcher.recruitment.years).join(', ')} 
                                    </> :
                                    'Accepting Graduate Students'
                                }
                            >
                                <Avatar size={avatarSize} style={styles.avatar} icon={<TeamOutlined />} />
                            </Tooltip>
                        </span>}

                        {researcher.C3I &&
                        <a 
                            style={{
                                top: profilePage?3:2,
                                position: "relative"
                            }}
                            className="sfu-grey-link"
                            href={"https://www.sfu.ca/climate-innovation.html"}
                            target="_blank"
                        >
                            <Tooltip
                                overlayClassName="sub-heading-font"
                                title='SFU CI Researcher'
                            >
                                <C3IIcon style={{fontSize: profilePage?28:20}}/>
                            </Tooltip>
                        </a>}
                    </Col>
                </Row>

                <Col>
                    {researcher.show_primary_campus && researcher.primary_campus && profilePage &&
                    <span className="sub-heading-font" style={styles.primary_campus}>
                        {researcher.primary_campus} Campus
                    </span>}
                </Col>
            </Row>

            {facultiesJSXElements}

            {researcher.interests &&
            <Row style={styles.interestsRow}>
                {interests}
            </Row>}
            {researcher.SDGvec &&
            <Row className="SDGvec" style={styles.SDGvecRow}>
                {researcher.SDGvec.map((status, i) => ({status, index:i}))
                .filter(item=>item.status)
                .map((item, index) => (
                    <div key={index}>
                        <Tooltip
                            overlayClassName="sub-heading-font"
                            title={SDGs[item.index].goal}
                            color={SDGIconBackgroundColors[item.index]}
                        >
                            <img
                                src={SDGs[item.index].icon}
                                alt={SDGs[item.index].goal} 
                                onClick={(event) => {
                                    onSDGInterestClick(item.index + 1);
                                }}
                                // title={SDGs[item.index].goal} 
                            />
                        </Tooltip>
                    </div>
                ))}
            </Row>}
        </div>
    )
    return title as ReactElement;
};

export default ResearcherListTitle;